import _ from "lodash";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/styles";
import {
  Checkbox,
  colors,
  Divider,
  FormHelperText,
  FormLabel,
  InputAdornment,
  Modal,
  TextField,
} from "@material-ui/core";
import { toast } from "@/design-system/components/Toast/Toast";
import PropTypes from "prop-types";
import { format } from "date-fns";
import { Plus, Search, EllipsisVertical } from "lucide-react";
import { useDispatch } from "react-redux";
import { useHotkeys } from "react-hotkeys-hook";

import { Table } from "@/design-system/components/Table/Table";
import { DropdownMenu } from "@/design-system/components/DropdownMenu/DropdownMenu";
import { ToggledEditableBlock } from "@/design-system/components/ToggledEditableBlock/ToggledEditableBlock";
import { Button } from "@/design-system/components/Button/Button";
import { useSetBreadcrumbs } from "@/NavBar/NavigationLayoutContext";
import { makeRequest, specifiedUserIsFPEmployee } from "./util";
import Label from "./Label";
import useAccountMetadata from "./hooks/useAccountMetadata";
import useUsers from "./hooks/useUsers";
import FreshpaintUser from "./components/users/FreshpaintUser";
import { UserDisplay } from "@/components/users/UserDisplay";
import { fetchUsers } from "./actions/users";
import DeleteInviteModal from "./DeleteInviteModal";
import useForms from "./forms/queries/useForms";
import { useAccountEntitlements } from "./entitlements/queries/useAccountEntitlements";
import useFormSubmissionAccess from "./access-control/queries/useFormSubmissionAccess";
import InviteUserModal from "./TeamPage/InviteUserModal";
import RoleSelector from "./TeamPage/RoleSelector";
import { roleToReadableName } from "./TeamPage/roleOptions";

import { updateCompanyDetails } from "@/account/api/company";
import { Card } from "@/design-system/components/Card/Card";
import { getCurrentUser } from "./auth/firebase";
import {
  allEnvironmentIDs,
  inviteUser,
  RoleAdmin,
  RoleInviteDefault,
} from "@/components/users/inviteUser";

function TeamPage() {
  const dispatch = useDispatch();

  const accountMetadata = useAccountMetadata();
  const usersState = useUsers();

  const [inviteUserEmail, setInviteUserEmail] = useState(null);
  const [inviteUserEmailHasError, setInviteUserEmailHasError] = useState(false);
  const [inviteUserRole, setInviteUserRole] = useState(RoleInviteDefault);
  const [inviteSelectedEnvs, setInviteSelectedEnvs] = useState(null);

  const [userToDelete, setUserToDelete] = useState(null);
  const [userToChangeRole, setUserToChangeRole] = useState(null);
  const [userToManageForms, setUserToManageForms] = useState(null);
  const [userToManageEnvs, setUserToManageEnvs] = useState(null);

  const [inviteToDelete, setInviteToDelete] = useState(null);

  const invites = useMemo(() => {
    if (usersState.loading) {
      return [];
    }
    return _(usersState.invitedUsers)
      .sortBy((invite) => invite.to_email)
      .value();
  }, [usersState]);

  const members = useMemo(() => {
    if (usersState.loading) {
      return null;
    }

    const currentUserEmail = getCurrentUser().email;

    const you = usersState.users.filter(
      (user) => user.email === currentUserEmail,
    );
    const otherUsers = _(usersState.users)
      .filter((user) => user.email !== currentUserEmail)
      .sortBy((user) => user.email)
      .value();

    return you.concat(otherUsers);
  }, [usersState]);

  const currentMember = useMemo(() => {
    let you;
    if (!usersState.loading) {
      const authUser = getCurrentUser();
      you = usersState.users.find((member) => member.uid === authUser.uid);
    }
    return new FreshpaintUser(you);
  }, [usersState]);

  const [loadingUserEnvs, setLoadingUserEnvs] = useState(true);
  const [userEnvs, setUserEnvs] = useState(null);

  const canEditCompanyInfo = useMemo(
    () => currentMember.hasAdminAccess(),
    [currentMember],
  );

  function fetchUserEnvs() {
    makeRequest("get_all_non_admin_envs")
      .then((response) => {
        const envUsersList = response.data;
        const out = {};
        for (let { uid, proj_id, env_id } of envUsersList) {
          const projMap = out[uid] || {};
          const envMap = projMap[proj_id] || {};
          envMap[env_id] = true;
          projMap[proj_id] = envMap;
          out[uid] = projMap;
        }
        setUserEnvs(out);
        setLoadingUserEnvs(false);
      })
      .catch((error) => {
        toast.error("Something went wrong. Unable to load page.");
        console.error(error);
      });
  }

  useEffect(() => {
    fetchUserEnvs();
  }, []);

  const handleCompanyNameUpdate = useCallback(async (v) => {
    try {
      await updateCompanyDetails({
        newName: v,
      });
    } catch (error) {
      toast.error("Something went wrong. Unable to update company name.");
      console.error(error);
    }
  }, []);

  function handleUserEnvAccessUpdate(uid, projectID, envID, hasAccess) {
    makeRequest("update_user_env", {
      uid,
      proj_id: projectID,
      env_id: envID,
      has_access: hasAccess,
    })
      .then(() => {
        fetchUserEnvs();
      })
      .catch((error) => {
        toast.error("Something went wrong. Unable to load page.");
        console.error(error);
      });
  }

  function handleInviteUser() {
    if (inviteUserEmailHasError) {
      toast.error("You must provide a valid email to send invite");
      return;
    }

    if (!inviteUserEmail) {
      toast.error("You must provide an email to send invite");
      return;
    }

    if (!inviteUserRole) {
      toast.error("You must provide a role to send invite");
      return;
    }

    let envIDs;
    if (inviteUserRole === RoleAdmin) {
      envIDs = [];
    } else if (!inviteSelectedEnvs) {
      // No explicit selection means "grant access to every environment".
      envIDs = allEnvironmentIDs(accountMetadata);
    } else {
      envIDs = _(inviteSelectedEnvs)
        .entries()
        .filter((e) => e[1])
        .map((e) => e[0])
        .value();
    }

    inviteUser({ email: inviteUserEmail, role: inviteUserRole, envIDs })
      .then(() => {
        setInviteUserEmail(null);
        setInviteUserEmailHasError(false);
        setInviteSelectedEnvs(null);
        setInviteUserRole(RoleInviteDefault);
        dispatch(fetchUsers());
        toast.success("Invite Sent");
      })
      .catch((error) => {
        if (error.response && error.response.status === 429) {
          toast.error("Too many requests, try again later.");
          return;
        }
        toast.error("Something went wrong. Unable to load page.");
        console.error(error);
      });
  }

  function handleDeleteInvite() {
    makeRequest("remove_user_invite", { invite_id: inviteToDelete.id })
      .then(() => {
        dispatch(fetchUsers());
        setInviteToDelete(null);
      })
      .catch((error) => {
        toast.error("Something went wrong. Unable to remove invite.");
        console.error(error);
      });
  }

  function handleDeleteUser() {
    makeRequest("remove_user", { uid: userToDelete.uid })
      .then(() => {
        dispatch(fetchUsers());
        setUserToDelete(null);
      })
      .catch((error) => {
        toast.error("Something went wrong. Unable to load page.");
        console.error(error);
      });
  }

  function handleRoleChange(selectedMember, newRole) {
    if (selectedMember.role === newRole) {
      return;
    }

    makeRequest("update_user_role", {
      uid: selectedMember.uid,
      role: newRole,
    })
      .then(() => {
        setUserToChangeRole(null);
        dispatch(fetchUsers());
      })
      .catch((error) => {
        if (error.response.status === 403) {
          toast.error(
            "You're not authorized to perform this action. Please reload page.",
          );
          return;
        }
        toast.error("Something went wrong. Unable to load page.");
        console.error(error);
      });
  }

  function handleFormSubmissionAccessSave(selectedMember, formIDs) {
    makeRequest("save_form_submission_access", {
      uid: selectedMember.uid,
      form_ids: formIDs,
    })
      .then(() => {
        toast.success("Form submission access saved");
      })
      .catch((error) => {
        if (error.response.status === 403) {
          toast.error(
            "You're not authorized to perform this action. Please reload page.",
          );
          return;
        }
        toast.error("Something went wrong. Unable to load page.");
        console.error(error);
      });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1>
          {canEditCompanyInfo ? (
            <ToggledEditableBlock onNewValue={handleCompanyNameUpdate}>
              {accountMetadata.companyDetails.name || "Team"}
            </ToggledEditableBlock>
          ) : (
            accountMetadata.companyDetails.name || "Team"
          )}
        </h1>

        <Button
          disabled={!currentMember.hasAdminAccess()}
          tooltipContent={
            !currentMember.hasAdminAccess()
              ? "You're not authorized to perform this action."
              : undefined
          }
          leadingIcon={<Plus />}
          onClick={() => {
            setInviteUserEmail("");
            setInviteUserEmailHasError(false);
            setInviteUserRole(RoleInviteDefault);
          }}
        >
          Add teammate
        </Button>
      </div>
      {invites.length > 0 && (
        <InvitesList
          currentMember={currentMember}
          invites={invites}
          onClickDelete={setInviteToDelete}
        />
      )}
      {members && (
        <MemberList
          currentMember={currentMember}
          members={members}
          onClickDelete={setUserToDelete}
          onClickChangeRole={setUserToChangeRole}
          onClickFormsAccess={setUserToManageForms}
          onClickEnvAccess={setUserToManageEnvs}
        />
      )}

      <InviteUserModal
        open={inviteUserEmail !== null}
        onClose={() => {
          setInviteUserEmail(null);
          setInviteUserEmailHasError(false);
          setInviteUserRole(RoleInviteDefault);
        }}
        onInvite={handleInviteUser}
        onEmailChange={setInviteUserEmail}
        inviteUserRole={inviteUserRole}
        onRoleChange={(newRole) => {
          setInviteUserRole(newRole);
        }}
        inviteSelectedEnvs={inviteSelectedEnvs}
        setInviteSelectedEnvs={setInviteSelectedEnvs}
        setHasEmailError={setInviteUserEmailHasError}
      />
      <ChangeUserRoleModal
        open={userToChangeRole !== null}
        onClose={() => {
          setUserToChangeRole(null);
        }}
        onRoleChange={(newRole) => {
          handleRoleChange(userToChangeRole, newRole);
        }}
        defaultRole={userToChangeRole?.role}
        member={userToChangeRole}
      />
      <FormAccessManagementModal
        open={userToManageForms !== null}
        onClose={() => {
          setUserToManageForms(null);
        }}
        onSave={(formIDs) => {
          handleFormSubmissionAccessSave(userToManageForms, formIDs);
        }}
        member={userToManageForms}
      />
      <EnvironmentAccessModal
        open={userToManageEnvs !== null}
        member={userToManageEnvs}
        onClose={() => setUserToManageEnvs(null)}
        loadingUserEnvs={loadingUserEnvs}
        userEnvs={userEnvs}
        enableToggle={currentMember.hasAdminAccess()}
        onUserEnvAccessUpdate={handleUserEnvAccessUpdate}
      />
      <DeleteUserModal
        open={userToDelete !== null}
        user={userToDelete}
        onClose={() => setUserToDelete(null)}
        onDelete={handleDeleteUser}
      />
      <DeleteInviteModal
        open={inviteToDelete !== null}
        invite={inviteToDelete}
        onClose={() => setInviteToDelete(null)}
        onDelete={handleDeleteInvite}
      />
    </div>
  );
}

const MemberListEmptyState = () => (
  <div className="text-textSubtle p-4 text-center text-sm">No teammates</div>
);

export const MemberList = React.memo(function MemberList({
  currentMember,
  members,
  onClickDelete,
  onClickChangeRole,
  onClickFormsAccess,
  onClickEnvAccess,
}) {
  const { data: accountEntitlements } = useAccountEntitlements();

  const isFormEnabled = () => {
    return accountEntitlements?.account_access?.some(
      (access) => access.entitlement_key === "forms",
    );
  };

  const columnConfigs = useMemo(
    () => [
      {
        label: "",
        CellContent: ({ row }) =>
          row.uid === currentMember.uid ? (
            <Label color="#3f51b5">You</Label>
          ) : null,
      },
      {
        label: "Email",
        CellContent: ({ row }) => <UserDisplay user={row} variant="stacked" />,
      },
      {
        label: "Role",
        CellContent: ({ row }) => (
          <div>
            <span>{roleToReadableName(row.role)}</span>
            {specifiedUserIsFPEmployee(row.uid) && (
              <p className="text-textSubtle text-xs">Member is a super-user</p>
            )}
          </div>
        ),
      },
      {
        label: "Created At",
        align: "center",
        getCellText: (row) => format(new Date(row.created_at), "MMMM d, yyyy"),
      },
      {
        label: "Actions",
        align: "center",
        CellContent: ({ row }) => (
          <PositionedActionsMenu
            hideRole={
              row.uid === currentMember.uid ||
              !currentMember.hasAdminAccess() ||
              specifiedUserIsFPEmployee(row.uid)
            }
            onClickRole={() => onClickChangeRole(row)}
            hideEnvAccess={false}
            onClickEnvAccess={() => onClickEnvAccess(row)}
            hideForms={
              !isFormEnabled() ||
              row.uid === currentMember.uid ||
              row.role === RoleAdmin ||
              !currentMember.hasAdminAccess() ||
              specifiedUserIsFPEmployee(row.uid)
            }
            onClickForms={() => onClickFormsAccess(row)}
            hideDelete={
              row.uid === currentMember.uid || !currentMember.hasAdminAccess()
            }
            onClickDelete={() => onClickDelete(row)}
          />
        ),
      },
    ],
    [
      currentMember,
      onClickChangeRole,
      onClickEnvAccess,
      onClickFormsAccess,
      onClickDelete,
      isFormEnabled,
    ],
  );

  return (
    <div className="space-y-4">
      <Table
        columnConfigs={columnConfigs}
        EmptyState={MemberListEmptyState}
        getRowId={(row) => row.uid}
        rows={members}
      />
      <p>
        Learn more about{" "}
        <a
          className="text-brand-red underline"
          href="https://documentation.freshpaint.io/your-freshpaint-account/settings/rbac"
        >
          role-based access control
        </a>
      </p>
    </div>
  );
});

const InvitesListEmptyState = () => (
  <div className="text-textSubtle p-4 text-center text-sm">
    No pending invites
  </div>
);

function InvitesList({ currentMember, invites, onClickDelete }) {
  const columnConfigs = useMemo(() => {
    const configs = [
      {
        label: "",
        CellContent: () => <Label color="#3f51b5">Pending</Label>,
      },
      {
        label: "Email",
        CellContent: ({ row }) => (
          <span className="font-semibold">{row.to_email}</span>
        ),
      },
      {
        label: "Created At",
        align: "center",
        getCellText: (row) => format(new Date(row.created_at), "MMMM d, yyyy"),
      },
    ];

    if (currentMember.hasAdminAccess()) {
      configs.push({
        label: "",
        align: "right",
        CellContent: ({ row }) => (
          <Button variant="destructive" onClick={() => onClickDelete(row)}>
            Delete
          </Button>
        ),
      });
    }

    return configs;
  }, [currentMember, onClickDelete]);

  return (
    <div className="space-y-2">
      <h2 className="text-base font-semibold">Invited teammates</h2>
      <Table
        columnConfigs={columnConfigs}
        EmptyState={InvitesListEmptyState}
        getRowId={(row) => row.to_email}
        rows={invites}
      />
    </div>
  );
}

const userEnvSelectorStyles = makeStyles(() => ({
  textCell: {
    textAlign: "right",
  },
  checkboxCell: {
    textAlign: "center",
  },

  noborder: {
    border: "none",
  },

  hidden: {
    visibility: "hidden",
  },
}));

const UserEnvironmentSelectorBase = ({
  getHasAccess,
  updateAccess,
  enableToggle,
  showAllProjectsEnvs,
  showProjectCheckbox,
}) => {
  const accountMetadata = useAccountMetadata();
  const classes = userEnvSelectorStyles();

  const projects = accountMetadata.projects.filter(
    (project) => project.environments.length,
  );

  const hasAccessProjAllEnvs = {};
  for (let project of projects) {
    hasAccessProjAllEnvs[project.ID] = _.every(project.environments, (env) =>
      getHasAccess(project.ID, env.ID),
    );
  }
  const hasAccessAllProjsAndEnvs = _.every(hasAccessProjAllEnvs);

  return (
    <table className="w-full text-sm">
      <tbody>
        {showAllProjectsEnvs && (
          <tr>
            <td className={classes.textCell}>
              <i>All Projects and Environments</i>
            </td>
            <td className={classes.checkboxCell}>
              <Checkbox
                checked={hasAccessAllProjsAndEnvs}
                disabled={!enableToggle}
                onChange={() => {
                  updateAccess(null, null, !hasAccessAllProjsAndEnvs);
                }}
              />
            </td>
          </tr>
        )}
        {projects.map((project, i) => {
          const isLastProj = i === projects.length - 1;
          const hasAccessAllEnvs = hasAccessProjAllEnvs[project.ID];

          return (
            <React.Fragment
              key={`user-env-selector-base-project-${project.ID}`}
            >
              <tr>
                <td className={clsx(classes.textCell, classes.noborder)}>
                  <b>{project.name}</b>
                </td>
                <td className={clsx(classes.checkboxCell, classes.noborder)}>
                  <Checkbox
                    className={clsx(!showProjectCheckbox && classes.hidden)}
                    checked={hasAccessAllEnvs}
                    disabled={!enableToggle}
                    onChange={() => {
                      updateAccess(project.ID, null, !hasAccessAllEnvs);
                    }}
                  />
                </td>
              </tr>
              {_.sortBy(project.environments, "createdAt").map(
                (environment, j) => {
                  const isLastEnv = j === project.environments.length - 1;
                  const showBorder = !isLastProj && isLastEnv;
                  const hasAccess = getHasAccess(project.ID, environment.ID);

                  return (
                    <React.Fragment
                      key={`user-env-selector-base-env-${environment.ID}`}
                    >
                      <tr>
                        <td
                          className={clsx(classes.textCell, {
                            [classes.noborder]: !showBorder,
                          })}
                        >
                          {environment.name}
                        </td>
                        <td
                          className={clsx(classes.checkboxCell, {
                            [classes.noborder]: !showBorder,
                          })}
                        >
                          <Checkbox
                            checked={hasAccess}
                            disabled={!enableToggle}
                            onChange={() => {
                              updateAccess(
                                project.ID,
                                environment.ID,
                                !hasAccess,
                              );
                            }}
                          />
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                },
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

const UserEnvironmentSelectorUsersTable = ({
  member,
  loadingUserEnvs,
  userEnvs,
  enableToggle,
  onUserEnvAccessUpdate,
}) => {
  const accountMetadata = useAccountMetadata();

  const isAdmin = member.role === RoleAdmin;
  if (!isAdmin && loadingUserEnvs) {
    return <>Loading</>;
  }

  const getHasAccess = (projID, envID) => {
    if (isAdmin) {
      return true;
    }
    const projMap = userEnvs[member.uid] || {};
    const envMap = projMap[projID] || {};
    return !!envMap[envID];
  };

  const updateAccess = (projID, envID, hasAccess) => {
    onUserEnvAccessUpdate(member.uid, projID, envID, hasAccess);
  };

  return (
    <UserEnvironmentSelectorBase
      getHasAccess={getHasAccess}
      updateAccess={updateAccess}
      enableToggle={!isAdmin && enableToggle}
      showAllProjectsEnvs={enableToggle && accountMetadata.projects.length > 0}
      showProjectCheckbox={enableToggle}
    />
  );
};

export const UserEnvironmentSelectorInviteModal = ({
  inviteSelectedEnvs,
  setInviteSelectedEnvs,
}) => {
  const accountMetadata = useAccountMetadata();
  const projects = accountMetadata.projects.filter(
    (project) => project.environments.length,
  );

  const getHasAccess = (_, envID) => {
    if (!inviteSelectedEnvs) {
      return true;
    }
    return !!inviteSelectedEnvs[envID];
  };

  const updateAccess = (projID, envID, hasAccess) => {
    let updatedEnvIDs;
    if (projID && envID) {
      updatedEnvIDs = [envID];
    } else {
      if (projID) {
        const project = projects.find((project) => project.ID === projID);
        if (project) {
          updatedEnvIDs = project.environments.map((env) => env.ID);
        }
      } else {
        updatedEnvIDs = _.flatten(
          projects.map((project) => project.environments.map((env) => env.ID)),
        );
      }
    }
    setInviteSelectedEnvs((prevEnvs) => {
      let envs;
      if (prevEnvs) {
        envs = { ...prevEnvs };
      } else {
        const allEnvIDs = _.flatten(
          projects.map((project) => project.environments.map((env) => env.ID)),
        );
        envs = {};
        for (let envID of allEnvIDs) {
          envs[envID] = true;
        }
      }

      for (let envID of updatedEnvIDs) {
        envs[envID] = hasAccess;
      }
      return envs;
    });
  };

  return (
    <UserEnvironmentSelectorBase
      getHasAccess={getHasAccess}
      updateAccess={updateAccess}
      enableToggle={true}
      showAllProjectsEnvs={true}
      showProjectCheckbox={true}
    />
  );
};

export const useModalStyles = makeStyles((theme) => ({
  root: {
    background: "white",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    outline: "none",
    boxShadow: theme.shadows[20],
    width: 500,
    maxHeight: "100%",
    overflowY: "auto",
    maxWidth: "100%",
  },
  container: {
    marginTop: theme.spacing(3),
    height: 200,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 2,
  },
  inputField: {
    marginTop: 16,
    overflowX: "wrap",
    wordBreak: "break-all",
  },
  deactivateButton: {
    color: theme.palette.common.white,
    backgroundColor: colors.red[600],
    "&:hover": {
      backgroundColor: colors.red[900],
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: colors.red[600],
      },
    },
  },
  closeIconAction: {
    "&:hover": {
      cursor: "pointer",
    },
    margin: 0,
  },
  bold: {
    fontWeight: 550,
  },
}));

function PositionedActionsMenu({
  hideRole,
  hideForms,
  hideDelete,
  hideEnvAccess,
  onClickRole,
  onClickForms,
  onClickDelete,
  onClickEnvAccess,
}) {
  const actions = useMemo(() => {
    const items = [];
    if (!hideRole) items.push({ label: "Edit Role", handler: onClickRole });
    if (!hideEnvAccess)
      items.push({
        label: "Edit Environment Access",
        handler: onClickEnvAccess,
      });
    if (!hideForms)
      items.push({ label: "Edit Forms Access", handler: onClickForms });
    if (!hideDelete) items.push({ label: "Delete", handler: onClickDelete });
    return items;
  }, [
    hideRole,
    hideForms,
    hideDelete,
    hideEnvAccess,
    onClickRole,
    onClickForms,
    onClickDelete,
    onClickEnvAccess,
  ]);

  if (actions.length === 0) return null;

  return (
    <DropdownMenu
      options={actions}
      getOptionLabel={(action) => action.label}
      onSelectOption={(action) => action.handler()}
      icon={<EllipsisVertical className="h-5 w-5" />}
      triggerProps={{ className: "h-8 w-8 rounded-md p-1.5 hover:bg-gray-100" }}
    />
  );
}

function EnvironmentAccessModal({
  open,
  member,
  onClose,
  loadingUserEnvs,
  userEnvs,
  enableToggle,
  onUserEnvAccessUpdate,
}) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)} headerText="Environment Access">
        <p className="font-semibold">Email</p>
        <p>{member?.email}</p>
        <br />
        {member && (
          <UserEnvironmentSelectorUsersTable
            member={member}
            loadingUserEnvs={loadingUserEnvs}
            userEnvs={userEnvs}
            enableToggle={enableToggle}
            onUserEnvAccessUpdate={onUserEnvAccessUpdate}
          />
        )}
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </Card>
    </Modal>
  );
}

function ChangeUserRoleModal({
  open,
  onClose,
  onRoleChange,
  defaultRole,
  member,
}) {
  const classes = useModalStyles();
  const [selectedRole, setSelectedRole] = useState(defaultRole);

  return (
    <Modal
      onClose={() => {
        onClose();
        setSelectedRole(defaultRole);
      }}
      open={open}
    >
      <Card className={clsx(classes.root)} headerText="Change role">
        <p className={clsx(classes.bold)} id="email">
          Email
        </p>
        <p>{member?.email}</p>
        <br />
        <RoleSelector
          defaultRole={defaultRole}
          onRoleChange={(newRole) => setSelectedRole(newRole)}
        />
        <div className={classes.actions}>
          <Button
            variant="ghost"
            onClick={() => {
              onClose();
              setSelectedRole(defaultRole);
            }}
          >
            Close
          </Button>
          <Button
            onClick={() => {
              onRoleChange(selectedRole);
            }}
          >
            Save
          </Button>
        </div>
      </Card>
    </Modal>
  );
}

ChangeUserRoleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRoleChange: PropTypes.func.isRequired,
};

function FormAccessManagementModal({ open, onClose, onSave, member }) {
  const classes = useModalStyles();
  const { data: forms } = useForms();
  const { data: formSubmissionsAccess } = useFormSubmissionAccess(member?.uid);
  const [selectedFormIDs, setSelectedFormIDs] = useState([]);
  const [formIDsPopulated, setFormIDsPopulated] = useState(false);
  const [allSelected, setAllSelected] = useState(false);

  if (!member || !formSubmissionsAccess) {
    return null;
  }

  function commonFormIDs(arr1, arr2) {
    return arr1.filter((element) => arr2.some((obj) => obj.id === element));
  }

  if (!formIDsPopulated && !!formSubmissionsAccess?.form_ids) {
    setFormIDsPopulated(true);
    setSelectedFormIDs(commonFormIDs(formSubmissionsAccess.form_ids, forms));
  }

  return (
    <Modal
      onClose={() => {
        setFormIDsPopulated(false);
        onClose();
      }}
      open={open}
    >
      <Card
        className={clsx(classes.root)}
        headerText="Form submission permissions"
      >
        <p className={clsx(classes.bold)} id="email">
          Email
        </p>
        <p>{member?.email}</p>
        <br />
        <FormAccessSelector
          onSelect={(formID) => {
            let i = selectedFormIDs.indexOf(formID);
            if (i > -1) {
              setSelectedFormIDs(selectedFormIDs.filter((f) => f !== formID));
            } else {
              setSelectedFormIDs([...selectedFormIDs, formID]);
            }
          }}
          onSelectAll={() => {
            if (!allSelected) {
              setSelectedFormIDs(forms.map((f) => f.id));
            } else {
              setSelectedFormIDs([]);
            }
            setAllSelected(!allSelected);
          }}
          allForms={forms}
          hasFullAccess={formSubmissionsAccess?.all}
          selectedFormSubmissionIDs={selectedFormIDs}
        />
        <Divider />
        <div className={classes.actions}>
          <Button
            variant="ghost"
            onClick={() => {
              setFormIDsPopulated(false);
              onClose();
            }}
          >
            Close
          </Button>
          <Button
            color="primary"
            onClick={() => {
              onSave(selectedFormIDs);
              setFormIDsPopulated(false);
              formSubmissionsAccess.form_ids = selectedFormIDs;
              onClose();
            }}
          >
            Save
          </Button>
        </div>
      </Card>
    </Modal>
  );
}

FormAccessManagementModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

function FormAccessSelector({
  onSelect,
  onSelectAll,
  hasFullAccess,
  allForms,
  selectedFormSubmissionIDs,
}) {
  const classes = useModalStyles();
  const [searchTerm, setSearchTerm] = useState("");

  let filteredForms = React.useMemo(
    () => _.sortBy([...allForms], (f) => f.name.toLowerCase()),
    [allForms],
  );

  filteredForms.sort((a, b) =>
    a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
  );

  const hasAccessToFormSubmissions = (formID) => {
    if (hasFullAccess) {
      return true;
    }

    return selectedFormSubmissionIDs.includes(formID);
  };

  const handleSearchTermChange = (term) => {
    setSearchTerm(term.trim());
  };

  if (searchTerm && searchTerm !== "") {
    filteredForms = _.filter(filteredForms, (form) => {
      return form.name.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1;
    });
  }

  return (
    <div>
      <FormLabel id="checkbox-form-select" className={clsx(classes.bold)}>
        Form Submissions ({selectedFormSubmissionIDs.length}/{allForms.length})
      </FormLabel>
      <FormHelperText style={{ fontSize: "0.8em", paddingBottom: "10px" }}>
        This teammate will be able to read form submissions for the selected
        forms
      </FormHelperText>
      <div className="max-h-[365px] overflow-scroll">
        <FormSearchInput
          key="search"
          initialSearch={searchTerm}
          onChange={handleSearchTermChange}
        ></FormSearchInput>
        <Button
          style={{ float: "right" }}
          variant="link"
          onClick={() => {
            onSelectAll();
          }}
        >
          Select All
        </Button>
        <table className="w-full text-sm">
          <tbody>
            {filteredForms?.map((form) => {
              return (
                <tr key={form.id}>
                  <td className={clsx(classes.textCell, classes.noborder)}>
                    <b>{form.name}</b>
                  </td>
                  <td className={clsx(classes.checkboxCell, classes.noborder)}>
                    <Checkbox
                      style={{ float: "right" }}
                      checked={hasAccessToFormSubmissions(form.id)}
                      onChange={() => {
                        onSelect(form.id);
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const FormSearchInput = ({ initialSearch, onChange }) => {
  const classes = useModalStyles();
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const inputRef = useRef();

  useEffect(() => {
    onChange(searchTerm);
  }, [searchTerm, onChange]);

  const setFocus = (e) => {
    e.preventDefault();

    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  };

  useHotkeys("ctrl+f", setFocus);
  useHotkeys("cmd+f", setFocus);

  return (
    <TextField
      style={{
        paddingBottom: "10px",
      }}
      variant="standard"
      name="search"
      autoComplete="off"
      onChange={(e) => setSearchTerm(e.target.value)}
      value={searchTerm}
      inputRef={inputRef}
      InputProps={{
        className: clsx(classes.searchField),
        placeholder: "Search",
        startAdornment: (
          <InputAdornment position="start">
            <Search className={clsx(classes.icon)} />
          </InputAdornment>
        ),
      }}
    />
  );
};

function DeleteUserModal({ open, user, onClose, onDelete }) {
  const classes = useModalStyles();

  const userString = user ? user.email : "this user";

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)} headerText="Remove teammate">
        <p className="text-sm">
          <span className={clsx(classes.bold)}>
            Are you sure you want to delete {userString} from your account?
          </span>
        </p>
        <Divider />
        <div className={classes.actions}>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="destructive"
            className={classes.deactivateButton}
            onClick={onDelete}
          >
            Delete User
          </Button>
        </div>
      </Card>
    </Modal>
  );
}

DeleteUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  user: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

const breadcrumbs = [
  {
    label: "Settings",
  },
  {
    label: "Team Members",
    href: "/settings/team",
  },
];

function TeamPageWithNavigation() {
  useSetBreadcrumbs(breadcrumbs);

  return <TeamPage />;
}

export default TeamPageWithNavigation;
